import { useCallback, useEffect, useRef, useState } from 'react';
import { Draggable, cn, dndKitCore } from '@divlab/divanui';

import styles from './DraggableDrawer.module.css';

import type { HTMLAttributes, FC, PropsWithChildren } from 'react';
import type { DragEndEvent, DragMoveEvent } from '@dnd-kit/core';

export interface DraggableDrawerProps extends HTMLAttributes<HTMLDivElement> {
  onHide?: () => void;
  initialHeight: string;
  containerRef: React.MutableRefObject<HTMLElement>;
  onHeightChange?: (size: number) => void;
  maxPercentageHeight?: number;
  disabled?: boolean;
  onlySwipeDown?: boolean;
}

const DraggableDrawer: FC<PropsWithChildren<DraggableDrawerProps>> = (props) => {
  const {
    onHide,
    children,
    onHeightChange,
    className,
    initialHeight,
    disabled,
    containerRef,
    maxPercentageHeight = 95,
    onlySwipeDown,
    ...restProps
  } = props;
  const { DndContext, useSensors, useSensor, MouseSensor, TouchSensor } = dndKitCore;
  const [drawerHeight, setDrawerHeight] = useState(0);
  const dragEndTimerRef = useRef<NodeJS.Timeout>(null);
  const dragEndNestedTimerRef = useRef<NodeJS.Timeout>(null);
  const drawerRef = useRef<HTMLDivElement>();
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 50,
        tolerance: 5,
      },
    }),
  );

  const handleDragStart = () => {
    setDrawerHeight(drawerRef.current.clientHeight);
    drawerRef.current.style.transition = 'height 0s ease';
  };

  const handleDragMove = (event: DragMoveEvent) => {
    const newHeight = drawerHeight - event.delta.y;
    if (onlySwipeDown && event.delta.y < 0) return;
    drawerRef.current.style.height = `${newHeight}px`;
  };

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const deltaY = event.delta.y;
      const maxContainerHeight = Math.floor(
        (containerRef.current.clientHeight * maxPercentageHeight) / 100,
      );

      const center = Math.floor((containerRef.current.clientHeight * 50) / 100);

      drawerRef.current.style.transition = 'height 0.3s ease';

      if (drawerHeight >= maxContainerHeight) {
        // Свайп вниз при открытой шторке
        if (deltaY > 100) {
          // Свайп вниз - скрываем шторку
          drawerRef.current.style.height = '0px';
          if (onHide) {
            dragEndNestedTimerRef.current = setTimeout(onHide, 300);
          }
        } else if (deltaY > 70) {
          //Немного сдвинули вниз - возвращаем к начальному состоянию
          drawerRef.current.style.height = initialHeight;
        } else {
          drawerRef.current.style.height = `${maxPercentageHeight}%`;
        }
        return;
      }

      if (drawerHeight <= center) {
        if (deltaY > 70) {
          drawerRef.current.style.height = '0px';
          if (onHide) {
            dragEndNestedTimerRef.current = setTimeout(onHide, 300);
          }
        } else if (deltaY < -70) {
          // Свайп вверх - раскрываем на всю высоту
          drawerRef.current.style.height = `${maxPercentageHeight}%`;
        } else {
          drawerRef.current.style.height = initialHeight;
        }
      }
    },
    [containerRef, drawerHeight, onHide, initialHeight, maxPercentageHeight],
  );

  const delayedDragEnd = (event: DragEndEvent) => {
    dragEndTimerRef.current = setTimeout(() => handleDragEnd(event), 0);
  };

  useEffect(() => {
    const drawer = drawerRef.current;
    let resizeObserver: ResizeObserver;

    const handleResize = async () => {
      if ('ResizeObserver' in window === false) {
        const resizeObserverModule = await import('@juggle/resize-observer');
        window.ResizeObserver = resizeObserverModule.ResizeObserver;
      }

      resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          if (entry.target === drawer) {
            if (onHeightChange) onHeightChange(entry.target.clientHeight);
          }
        }
      });

      if (drawer) {
        resizeObserver.observe(drawerRef.current);
      }
    };
    handleResize();

    return () => {
      if (drawer) {
        resizeObserver.unobserve(drawer);
      }
      clearTimeout(dragEndTimerRef.current);
      clearTimeout(dragEndNestedTimerRef.current);
    };
  }, [drawerRef, onHeightChange, disabled]);

  useEffect(() => {
    if (!disabled) {
      drawerRef.current.style.height = initialHeight;
    }
  }, [initialHeight, disabled]);

  return disabled ? (
    <div {...restProps} className={className}>
      {children}
    </div>
  ) : (
    <DndContext
      onDragStart={handleDragStart}
      onDragEnd={delayedDragEnd}
      onDragMove={handleDragMove}
      sensors={sensors}
    >
      <div {...restProps} ref={drawerRef} className={cn(className, styles.drawer)}>
        <div className={styles.dragAreaContainer}>
          <Draggable id='drawer' className={styles.dragArea}></Draggable>
        </div>
        <div className={styles.anchorHandle}></div>

        <div className={styles.content}>{children}</div>
      </div>
    </DndContext>
  );
};

export default DraggableDrawer;
